import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useConnectorRuns,
  useDecideConnectorRun,
  useResumeConnectorRun,
} from '@/hooks/useDataConnectors';

import type { DataConnectorRun } from '@repo/shared-types';

const statusVariant = (s: DataConnectorRun['status']) =>
  s === 'success' ? 'default' : s === 'failed' ? 'destructive' : 'secondary';

const STATUS_LABEL: Record<DataConnectorRun['status'], string> = {
  running: 'running',
  success: 'success',
  failed: 'failed',
  needs_approval: 'needs your decision',
};

const duration = (r: DataConnectorRun) => {
  const end = r.finished_at ? new Date(r.finished_at).getTime() : Date.now();
  const mins = Math.round((end - new Date(r.started_at).getTime()) / 60_000);
  return mins < 1 ? '<1 min' : `${mins} min`;
};

/** Last runs of one connector, with progress and a Resume action for failed runs. */
export const RunHistory = ({ connectorId }: { connectorId: string }) => {
  const { data: runs, isLoading } = useConnectorRuns(connectorId);
  const resume = useResumeConnectorRun();
  const decide = useDecideConnectorRun();
  const hasRunning = runs?.some((r) => r.status === 'running');

  if (isLoading) return <p className="text-muted-foreground text-sm">Loading runs…</p>;
  if (!runs?.length) return <p className="text-muted-foreground text-sm">No runs yet.</p>;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Started</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Progress</TableHead>
          <TableHead className="text-right">Requests</TableHead>
          <TableHead className="text-right">Saved</TableHead>
          <TableHead className="text-right">Skipped</TableHead>
          <TableHead className="text-right">Retired</TableHead>
          <TableHead>Notes</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((r, i) => (
          <TableRow key={r.id}>
            <TableCell className="text-sm whitespace-nowrap">
              {new Date(r.started_at).toLocaleString('en-GB')}
              <div className="text-muted-foreground text-xs">
                {r.trigger} · {duration(r)}
              </div>
            </TableCell>
            <TableCell>
              <Badge variant={statusVariant(r.status)}>{STATUS_LABEL[r.status]}</Badge>
            </TableCell>
            <TableCell className="min-w-32">
              <Progress value={r.slices_total ? (r.slices_done / r.slices_total) * 100 : 0} />
              <div className="text-muted-foreground mt-1 text-xs">
                {r.slices_done}/{r.slices_total} groups
              </div>
            </TableCell>
            <TableCell className="text-right">{r.requests.toLocaleString('en-GB')}</TableCell>
            <TableCell className="text-right">{r.upserted.toLocaleString('en-GB')}</TableCell>
            <TableCell className="text-right">
              {r.records_skipped.toLocaleString('en-GB')}
            </TableCell>
            <TableCell className="text-right">
              {r.marked_unavailable.toLocaleString('en-GB')}
            </TableCell>
            <TableCell className="max-w-md space-y-1 text-xs">
              {r.error && <p className="text-destructive break-words">{r.error}</p>}
              {r.status === 'failed' && r.retryable && r.resume_count < 3 && (
                <p className="text-muted-foreground">
                  Temporary error — will resume automatically ({r.resume_count}/3 tries used).
                </p>
              )}
              {Object.keys(r.unmatched_values ?? {}).length > 0 && (
                <p className="text-amber-700 dark:text-amber-400">
                  Didn&apos;t match our fixed values (add translations in the connector):{' '}
                  {Object.entries(r.unmatched_values)
                    .flatMap(([field, values]) =>
                      Object.entries(values).map(
                        ([value, count]) => `${field.split('.')[1]}: ${value} ×${count}`,
                      ),
                    )
                    .join(', ')}
                </p>
              )}
              {r.truncated_slices.length > 0 && (
                <p className="text-amber-700 dark:text-amber-400">
                  Over the paging limit (only partly fetched): {r.truncated_slices.join(', ')}
                </p>
              )}
              {/* Runs are newest first: only the newest one can still be decided. */}
              {r.status === 'needs_approval' && i > 0 && (
                <p className="text-muted-foreground">
                  {r.pending_retire.toLocaleString('en-GB')} cars were no longer listed — replaced
                  by a newer run, no decision needed.
                </p>
              )}
              {r.status === 'needs_approval' && i === 0 && (
                <div className="space-y-2 rounded-md border border-amber-300 p-2 dark:border-amber-700">
                  <p>
                    {r.pending_retire.toLocaleString('en-GB')} cars are no longer listed by this
                    source (over 20% of its cars — often because the connector settings changed).
                    Mark them unavailable?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={decide.isPending}
                      onClick={() => decide.mutate({ id: connectorId, runId: r.id, approve: true })}
                    >
                      Mark unavailable
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={decide.isPending}
                      onClick={() =>
                        decide.mutate({ id: connectorId, runId: r.id, approve: false })
                      }
                    >
                      Keep them
                    </Button>
                  </div>
                </div>
              )}
              {r.retire_decision && (
                <p className="text-muted-foreground">
                  {r.retire_decision === 'approved'
                    ? 'You chose to mark the cars no longer listed as unavailable.'
                    : 'You chose to keep the cars no longer listed.'}
                </p>
              )}
              {r.retire_decision === 'kept' && i === 0 && !hasRunning && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: connectorId, runId: r.id, approve: true })}
                >
                  {r.pending_retire > 0
                    ? `Mark the ${r.pending_retire.toLocaleString('en-GB')} unlisted cars unavailable now`
                    : 'Mark the unlisted cars unavailable now'}
                </Button>
              )}
              {/* Resume only helps temporary errors; settings errors need the connector fixed. */}
              {r.status === 'failed' && r.retryable && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={resume.isPending || hasRunning}
                  onClick={() => resume.mutate({ id: connectorId, runId: r.id })}
                >
                  Resume from where it stopped
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
